import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, mapTo, switchMap, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from './app.config';
import { AuthStatus } from './auth/auth.model';
import { AuthService } from './auth/auth.service';
import { CoachingEventCentreHookService } from './coaching-base/command/event-centre-hook.service';
import { CoachingTranscriptImportHookService } from './coaching/transcript-import-focus-dialog/transcript-import-hook.service';
import { determineDefaultLocale } from './i18n/i18n.model';
import { I18nService } from './i18n/i18n.service';
import { I18nLoaderService } from './i18n/loader.i18n.service';
import { NavService } from './nav/nav.service';
import { NewRelicService } from './new-relic/new-relic.service';
import {
    CPopEventCentreHookService,
    EventCentreDialogService,
} from './notification/event-centre';
import { epicDependencies } from './store';
import { singleCheckBuildNumber } from './system';
import { TeamsCallEventCentreHookService } from './teams-call';
import { AccountStatus } from './user/user.model';
import { UserService } from './user/user.service';

@Injectable()
export class AppInitService {
    constructor(
        private readonly _injector: Injector,
        private readonly _http: HttpClient,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private _i18nService: I18nService,
        private _i18nLoaderService: I18nLoaderService,
        private readonly _userService: UserService,
        private readonly _navService: NavService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        epicDependencies.injector = _injector;

        // TODO init hooks
        _injector.get<NewRelicService>(NewRelicService);

        // TODO init hooks
        _injector.get<CoachingEventCentreHookService>(
            CoachingEventCentreHookService,
        );

        // TODO init hooks
        _injector.get<CPopEventCentreHookService>(CPopEventCentreHookService);

        // TODO init hooks
        _injector.get<EventCentreDialogService>(EventCentreDialogService);

        // TODO init hooks
        _injector.get<TeamsCallEventCentreHookService>(
            TeamsCallEventCentreHookService,
        );

        // TODO init hooks
        _injector.get<CoachingTranscriptImportHookService>(
            CoachingTranscriptImportHookService,
        );
    }

    preRun(): Observable<any> {
        return of(null).pipe(
            switchMap(() => singleCheckBuildNumber(this._http)),
            switchMap(() => this._authService.checkToken()),
            switchMap(() =>
                this._i18nService.listLanguages(true).pipe(
                    tap((languages) => {
                        // TODO revise
                        this._translateService.addLangs(
                            languages.map((value) => value.id.toString()),
                        );
                    }),
                    switchMap(() => {
                        if (this._translateService.getDefaultLang()) {
                            return of(true);
                        }

                        let locale;
                        let lang: string;

                        if (
                            this._authService.authStatus.value ===
                            AuthStatus.AUTHENTICATED
                        ) {
                            try {
                                lang = String(
                                    this._authService.user.token
                                        .decodedAccessToken.payload.langId,
                                );
                                const langId = parseInt(lang);
                                locale = this._i18nService.languages.find(
                                    (e) => e.id === langId,
                                ).initial; // TODO revise. this isn't expected to ever fail
                            } catch {}
                        }

                        if (!locale || !lang) {
                            // translateService has a couple helper fns, but skip that
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            const pair = determineDefaultLocale(
                                this._i18nService.locale ||
                                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                                    this._appConfig.DEFAULT_LANGUAGE ||
                                    navigator.language ||
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    navigator['browserLanguage'],
                                this._i18nService.languages,
                            );
                            locale = pair.locale;
                            lang = pair.lang;
                        }

                        this._i18nService.locale = locale;

                        this._i18nLoaderService.disabled = true; // make TranslateLoader.getTranslation return immediately
                        this._translateService.setDefaultLang(lang); // english. calls TranslateLoader.getTranslation
                        this._i18nLoaderService.disabled = false;
                        this._translateService.resetLang(lang);
                        return this._translateService.use(lang);
                    }),
                    tap(() => {
                        let result = true;
                        if (!this._translateService.translations) {
                            result = false;
                        }
                        const values = Object.values(
                            this._translateService.translations,
                        );
                        if (
                            values.length === 0 ||
                            values.every(
                                (e) => e == null || Object.keys(e).length === 0,
                            )
                        ) {
                            result = false;
                        }
                        if (!result) {
                            throw new Error('no translations');
                        }
                    }),
                ),
            ),
        );
    }

    run(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const catchFunc = (error) => {
            console.error('init: error=%s', error);
            console.log(error);
            return of(false);
        };

        const status = new Subject<string>();

        // TODO invoke authService on first run to check for/load token then pull langId from there, rather than authService loading it on it's own. ie take some responsibility away from app.component

        console.log('init: authStatus=%d', this._authService.authStatus.value);
        const obss = [
            of(null).pipe(
                switchMap(() => {
                    const lang = String(
                        this._authService.user.token.decodedAccessToken.payload
                            .langId,
                    );
                    if (this._translateService.currentLang === lang) {
                        return of(null);
                    }
                    const langId = parseInt(lang);
                    this._i18nService.locale = this._i18nService.languages.find(
                        (e) => e.id === langId,
                    ).initial; // TODO revise. this isn't expected to ever fail
                    return this._translateService.use(lang);
                }),
                mapTo(true),
            ),
            this._userService.retrieve().pipe(
                tap((user) => this._authService.user.merge(user)),
                switchMap(() =>
                    forkJoin([
                        this._userService
                            .retrieveSecurityInfo(this._authService.user.id)
                            .pipe(
                                tap((securityInfo) => {
                                    if (
                                        securityInfo.accountStatus ===
                                        AccountStatus.LOCKED
                                    ) {
                                        throw new Error('locked out');
                                    }
                                    this._authService.user.securityInfo =
                                        securityInfo;
                                }),
                            ),
                        this._navService.reload(true) ??
                            this._navService.list(
                                this._authService.user.id,
                                true,
                            ),
                        this._navService
                            .retrieveLandingUserConfig(this._authService.user.id)
                            .pipe(
                                tap((landingRoute) => {
                                    this._authService.user.landingConfig =
                                        landingRoute;
                                }),
                            ),
                    ]),
                ),
                map(() => true),
            ),
            this._authService.permissions().pipe(
                // eslint-disable-next-line no-return-assign
                tap(
                    (permissions) =>
                        (this._authService.user.permissions = permissions),
                ),
                map(() => true),
            ),
        ];

        // TODO this needs to be revised
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        forkJoin(obss)
            .pipe(
                catchError(catchFunc),
                map((result) => {
                    console.debug('init: done errorOccurred=%o', !result);
                    if (!result) {
                        status.error(result);
                    } else {
                        status.complete();
                    }
                    return result;
                }),
            )
            .toPromise();

        return status;
    }
}
